import { injectable } from "inversify";
import ICustomerService from "./ICustomerService";
import ContractCountDTO from "features/dashboard/contracts/dtos/ContractCountDTO";
import AlertSimplified from "features/shared/alerts/dtos/AlertSimplified";
import CustomerSimplifiedDTO from "features/customer/dtos/CustomerSimplifiedDTO";
import IRepository from "data/IRepository";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import IMessagesService from "features/messages/IMessagesService";
import { UserRole } from "infrastructure/oidc/userRole";
import { Dashboard_Alerts, Dashboard_ContractsCount, Enums_RequirementType, UIConfig_SidebarMenu } from "data/endpoints";
import { store } from "infrastructure/store/ConfigureStore";
import { AlertsAction, setAlerts } from "infrastructure/store/alerts/AlertsActions";
import { ContractsCountAction, setContractsCount } from "infrastructure/store/contractsCount/ContractsCountActions";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { getQueryParams, getSummaryCountsParams } from "features/shared/helpers/queryParams";
import { CustomerActions, setCustomer } from "infrastructure/store/customer/CustomerActionTypes";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import IDataFilterService from "features/shared/components/dataFilter/IDataFilterService";
import { BoolEnum } from "features/shared/enums/boolEnum";
import IUrlParam from "features/shared/components/dataFilter/IUrlParam";
import { DataGridId } from "features/shared/components/dataGrid/DataGridId";
import PortalConfiguration from "infrastructure/configuration/PortalConfiguration";
import { loadPortalConfig } from "features/shared/helpers/modules";
import { DataGridAction, resetAllGrids } from "infrastructure/store/dataGrid/DataGridActions";
import RequirementTypeEnumDTO from "features/requirements/dtos/RequirementTypeEnumDTO";
import SidebarMenuItem from "features/shared/layout/sidebar/models/SidebarMenuItem";
import { SidebarAction, setSidebarMenuItems } from "infrastructure/store/sidebar/SidebarActions";
import { RequirementTypesAction, setRequirementTypes } from "infrastructure/store/requirementTypes/RequirementTypesActions";

@injectable()
export default class CustomerService implements ICustomerService {
  private readonly _alertsRepository: IRepository<AlertSimplified>;
  private readonly _contractsCountRepository: IRepository<ContractCountDTO>;
  private readonly _requirementTypesRepo: IRepository<RequirementTypeEnumDTO>;
  private readonly _sidebarMenuRepo: IRepository<SidebarMenuItem>;
  private readonly _messagesService: IMessagesService;
  private readonly _dataFilterService: IDataFilterService;

  private readonly _portalConfig: PortalConfiguration;

  constructor() {
    this._alertsRepository = inversifyContainer.get<IRepository<AlertSimplified>>(IocTypes.Repository);
    this._contractsCountRepository = inversifyContainer.get<IRepository<ContractCountDTO>>(IocTypes.Repository);
  this._requirementTypesRepo = inversifyContainer.get<IRepository<RequirementTypeEnumDTO>>(IocTypes.Repository);
  this._sidebarMenuRepo = inversifyContainer.get<IRepository<SidebarMenuItem>>(IocTypes.Repository);
    this._messagesService = inversifyContainer.get<IMessagesService>(IocTypes.MessagesService);
    this._dataFilterService = inversifyContainer.get<IDataFilterService>(IocTypes.DataFilterService);

    this._portalConfig = loadPortalConfig();
  }

  //#region public
  async loadAndStoreCustomerData(customer: CustomerSimplifiedDTO, adminRole: UserRole): Promise<void> {
    if (customer.customerId) {
      await Promise.all([
        this.loadAndStoreAlerts(customer.customerId),
        this.loadAndStoreContractsCount(customer.customerId),
        this.loadAndStoreRequirementTypes(customer.customerId),
        this.loadAndStoreSidebarMenu(customer.customerId),
        this._messagesService.loadAndStoreMessagesSimplified(customer.customerId)
      ]);

      store.dispatch<DataGridAction>(resetAllGrids()); // reset all setted grids
      this.storeCustomer(customer, adminRole);
    }
  }

  clearCustomerDataFromStore(): void {
    store.dispatch<ContractsCountAction>(setContractsCount([]));
    this.storeCustomer({});
  }

  storeContractsFilterParams(commodityDetail: CommodityDetail, isProduction?: boolean | undefined): void {
    let dataGridId = this._portalConfig.useContractualAccounts
      ? DataGridId.contractualAccountsGrid
      : DataGridId.contractGrid;

      let params: IUrlParam[] = [];

      if (commodityDetail) {
        params.push({ key: "CommodityDetail", value: commodityDetail });
      }
      if (isProduction) {
        params.push({ key: "IsProduction", value: BoolEnum.True });
      }
  
      this._dataFilterService.storeSetParams(dataGridId, params);
  }

  async loadAndStoreAlerts(customerId: string): Promise<void> {
    let alerts = await this.fetchAlerts(customerId);
    store.dispatch<AlertsAction>(setAlerts(alerts));
  }

  async loadAndStoreRequirementTypes (customerId: string): Promise<void> {
    let requirementTypes = await this.fetchRequirementTypes(customerId);
    store.dispatch<RequirementTypesAction>(setRequirementTypes(requirementTypes));
  }
  
  async loadAndStoreSidebarMenu(customerId: string): Promise<void> {
    let menuItems = await this.fetchSidebarMenu(customerId);
    store.dispatch<SidebarAction>(setSidebarMenuItems(menuItems));
  }
  //#region public

  //#region private
  private storeCustomer(customer: CustomerSimplifiedDTO, adminRole?: UserRole): void {
    if (adminRole === "admin-reader") {
      let customerCopy: CustomerSimplifiedDTO = {...customer};
      customerCopy.roles = [];
  
      store.dispatch<CustomerActions>(setCustomer(customerCopy));
    } else {
      store.dispatch<CustomerActions>(setCustomer(customer));
    }
  }

  private async fetchAlerts(customerId: string): Promise<AlertSimplified[]> {
    let alerts = await this._alertsRepository.get({
      url: Dashboard_Alerts,
      routeParams: getRouteParams("customerId", customerId)
    });

    return alerts;
  }

  private async loadAndStoreContractsCount(customerId: string): Promise<void> {
    let contractsCount = await this.fetchContractsCount(customerId);
    store.dispatch<ContractsCountAction>(setContractsCount(contractsCount));
  }

  private async fetchContractsCount(customerId: string): Promise<ContractCountDTO[]> {
    let contractsCount = await this._contractsCountRepository.get({
      url: Dashboard_ContractsCount,
      routeParams: getRouteParams("customerId", customerId),
      params: getSummaryCountsParams()
    });

    return contractsCount;
  }

  private fetchRequirementTypes = async (customerId: string): Promise<RequirementTypeEnumDTO[]> => {
    const result = await this._requirementTypesRepo.get({
      url: Enums_RequirementType,
      routeParams: getRouteParams("customerId", customerId)
    });

    return result;
  }
  
  private fetchSidebarMenu = async (customerId: string): Promise<SidebarMenuItem[]> => {
    const result = await this._sidebarMenuRepo.get({
      url: UIConfig_SidebarMenu,
      params: getQueryParams("customerId", customerId)
    });
    return result;
  }
  //#endregion private
}